import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearForm } from "../../../Common/Kendo/kendo_common";
import {
  setFormFields,
  setPrevFormFields,
} from "../../../API_Service/Settings_Slice";
import Toast from "../../../Common/Toastify";

// Ported from Grove's formbuilder field-edit modal
// (SRC_React(SmartHR_Theme)/GROVE_APP/.../Settings/appConfig/FormBuilderField.js) —
// same label/required/options editing logic. Two real changes:
//   - Grove fetches each field's dropdown options from the server
//     (commonGetConfigDataList("PIFORMOPTION", fieldID)) and saves back
//     through AppConfig_CreateUpdatePIFormField; options here are read
//     from/written back to the field's own local `options` (comma-separated)
//     string in the slice instead, since that's what
//     Save_ShiftChangeOverColumns / Get_ShiftChangeOverColumnList carry.
//   - The label input and "Is Required" checkbox were KendoTextField/
//     KendoCheckBox (kendo_common.js) — Kendo's own widget chrome, without
//     Bootstrap's layout classes to place it, rendered barely visible
//     (a plain, badly-spaced checkbox). Swapped for plain styled elements
//     matching this app's own input/checkbox look everywhere else; the
//     save/validation logic underneath is untouched.
const iCls =
  "w-full h-11 bg-[#f1f4f4] rounded-xl px-4 text-sm font-medium text-[#181c1d] border border-transparent focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all";

const FormBuilderField = ({ toggleFieldModal }) => {
  const dispatch = useDispatch();
  const createInitOption = () => ({ text: null, value: 0 });
  const [fieldLabel, setfieldLabel] = useState(null);
  const [isRequired, setIsRequired] = useState(false);
  const selectedFieldDetail = useSelector(
    (state) => state?.settings?.selectedFieldDetail
  );
  const IsOptionsRequred = useSelector(
    (state) => state?.settings?.isOptionsRequred
  );
  const prevFormFields = useSelector(
    (state) => state?.settings?.prevFormFields
  );
  const [fieldOptions, setFieldOptions] = useState([]);

  useEffect(() => {
    setfieldLabel(selectedFieldDetail?.label);
    setIsRequired(selectedFieldDetail?.required);
    GetDropDownDataList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const GetDropDownDataList = () => {
    const opts = selectedFieldDetail?.options
      ? selectedFieldDetail.options
          .split(",")
          .map((text, i) => ({ text, value: i + 1 }))
      : [];
    setFieldOptions(opts);
  };

  const handleAddOption = useCallback(() => {
    setFieldOptions([...fieldOptions, createInitOption()]);
  }, [fieldOptions]);

  const handleOptionOnchange = useCallback(
    (value, index) => {
      const updated = [...fieldOptions];
      updated[index] = { ...updated[index], text: value };
      setFieldOptions(updated);
      const hasDuplicate = fieldOptions.some(
        (opt) => opt.text?.trim().toLowerCase() == value.trim().toLowerCase()
      );

      if (hasDuplicate && value) {
        Toast("Duplicate option value found in the list.", "warning");
        return;
      }
    },
    [fieldOptions]
  );

  const handleSubmit = useCallback(
    (event) => {
      const fieldLabelValue = fieldLabel?.trim().toLowerCase();
      const labels = [
        ...prevFormFields
          .filter((opt) => opt.id !== selectedFieldDetail?.id)
          .map((opt) => opt.label?.trim().toLowerCase())
          .filter(Boolean),
      ];

      if (fieldLabelValue && labels.includes(fieldLabelValue)) {
        labels.push(fieldLabelValue);
      }

      const hasDuplicateLabels = new Set(labels).size !== labels.length;
      if (hasDuplicateLabels) {
        Toast("Field Label already exists", "warning");
        return;
      }
      const names = fieldOptions.map((opt) => opt.text?.trim().toLowerCase());
      const hasDuplicateOptions = new Set(names).size !== names.length;

      if (hasDuplicateOptions) {
        Toast("Duplicate option value found in the list.", "warning");
        return;
      }

      if (selectedFieldDetail != null) {
        event?.preventDefault?.();
        const updatedField = {
          ...selectedFieldDetail,
          label: fieldLabel,
          required: isRequired,
          options: IsOptionsRequred
            ? fieldOptions.map((o) => o.text).filter(Boolean).join(",")
            : undefined,
        };
        const updatedFormFields = prevFormFields.map((f) =>
          f.id === updatedField.id ? updatedField : f
        );
        dispatch(setFormFields(updatedFormFields));
        dispatch(setPrevFormFields(updatedFormFields));
        Toast("Field updated", "success");
        toggleFieldModal();
      }
    },
    [fieldOptions, fieldLabel, isRequired] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleDeleteOption = async (optionID, index) => {
    const updatedOptions = fieldOptions.filter((_, i) => i !== index);
    setFieldOptions(updatedOptions);
  };

  const FieldNameOnChange = (e) => {
    setfieldLabel(e);
    const hasDuplicate = prevFormFields.some(
      (opt) =>
        opt.label?.trim().toLowerCase() == e.trim().toLowerCase() &&
        opt.id != selectedFieldDetail?.id
    );

    if (hasDuplicate && e) {
      Toast("Field Label already exists", "warning");
      return;
    }
  };

  const toggleBuilderFieldModal = () => {
    toggleFieldModal();
    clearForm("formBuilderField-Form");
  };

  return (
    <form
      id="formBuilderField-Form"
      className="flex-1 overflow-y-auto flex flex-col min-h-0"
    >
      <div className="flex-1 overflow-y-auto px-7 py-6 space-y-4">
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: "10px",
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#08757C",
                marginBottom: "6px",
              }}
            >
              Field Label <span style={{ color: "#e5566a" }}>*</span>
            </label>
            <input
              type="text"
              className={iCls}
              value={fieldLabel ?? ""}
              onChange={(e) => FieldNameOnChange(e.target.value)}
              placeholder="e.g. Equipment Status"
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 14px",
              background: isRequired ? "#e4f2f2" : "#f7fafa",
              border: `1px solid ${isRequired ? "#08757C" : "#e5e9e9"}`,
              borderRadius: "12px",
              cursor: "pointer",
              userSelect: "none",
              transition: "background .15s ease, border-color .15s ease",
            }}
          >
            <input
              type="checkbox"
              checked={!!isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              style={{
                width: "18px",
                height: "18px",
                accentColor: "#08757C",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "13px", fontWeight: 700, color: "#181c1d" }}>
              Required field
            </span>
            <span style={{ fontSize: "11px", color: "#9eabac", marginLeft: "auto" }}>
              {isRequired ? "Officers must fill this in" : "Optional"}
            </span>
          </label>

          {IsOptionsRequred && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "10px",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#08757C",
                  }}
                >
                  Options
                </label>
                <button
                  type="button"
                  onClick={handleAddOption}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "11px",
                    fontWeight: 800,
                    color: "#08757C",
                    background: "transparent",
                    border: "1px solid #08757C",
                    borderRadius: "9px",
                    padding: "5px 10px",
                    cursor: "pointer",
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
                    add
                  </span>
                  Add
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  maxHeight: "220px",
                  overflowY: "auto",
                  paddingRight: "2px",
                }}
              >
                {fieldOptions?.length === 0 && (
                  <p style={{ fontSize: "12px", color: "#9eabac", fontStyle: "italic", margin: 0 }}>
                    No options yet — click Add.
                  </p>
                )}
                {fieldOptions?.map((option, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px",
                      background: "#f7fafa",
                      border: "1px solid #e5e9e9",
                      borderRadius: "10px",
                    }}
                  >
                    <span
                      style={{
                        width: "26px",
                        height: "26px",
                        flexShrink: 0,
                        borderRadius: "50%",
                        background: "#e4f2f2",
                        color: "#08757C",
                        fontSize: "11px",
                        fontWeight: 800,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {index + 1}
                    </span>

                    <input
                      type="text"
                      className={`${iCls} h-9`}
                      value={option?.text ?? ""}
                      onChange={(e) => handleOptionOnchange(e.target.value, index)}
                      placeholder={`Option ${index + 1}`}
                    />

                    <button
                      type="button"
                      onClick={() => handleDeleteOption(option?.value, index)}
                      title="Remove option"
                      style={{
                        flexShrink: 0,
                        width: "30px",
                        height: "30px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        background: "transparent",
                        color: "#3e494a",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: "17px" }}>
                        delete
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer — same shell as ShiftChangeOverForm.js: Cancel left, primary
          action right (flex-1, filled teal). */}
      <div className="px-7 pb-6 pt-2 flex gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={toggleBuilderFieldModal}
          className="px-5 h-11 text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] rounded-xl border border-[#e5e9e9] transition-all"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="flex-1 flex items-center justify-center gap-2 text-white font-bold text-sm rounded-xl hover:brightness-110 transition-all"
          style={{ background: "#08757C", height: "44px" }}
        >
          <span className="material-symbols-outlined text-[17px]">save</span>
          Save
        </button>
      </div>
    </form>
  );
};
export default memo(FormBuilderField);
